import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { apiHelper } from '../utils/apiHelper';
import { detectProfanity, filterProfanity } from '../utils/profanityFilter';
import styled from 'styled-components';
import { getCsrfToken } from '../utils/csrfToken';

const AdminContainer = styled.div`
  padding: 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 12px 16px;
  font-size: 1rem;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--primary-color);
  outline: none;
  transition: border-color 0.3s ease;
  flex: 1;
  min-width: 200px;
  
  &:focus {
    border-color: var(--gradient-pink);
  }
  
  &::placeholder {
    color: var(--secondary-color);
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  font-size: 1rem;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--primary-color);
  outline: none;
  cursor: pointer;
  
  &:focus {
    border-color: var(--gradient-pink);
  }
`;

const CommentsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: var(--primary-color);
  background: var(--glass-bg);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 15px;
  border-bottom: 2px solid var(--gradient-pink);
  background: rgba(255, 255, 255, 0.05);
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
  max-width: 300px;
  word-wrap: break-word;
`;

const CommentText = styled.div`
  max-height: 100px;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.5;
  
  &.spoiler {
    filter: blur(5px);
    cursor: pointer;
    
    &:hover {
      filter: blur(0);
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 8px 15px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const EditButton = styled(Button)`
  background: var(--gradient-cyan);
  color: #111;
  
  &:hover {
    box-shadow: 0 3px 10px rgba(78, 205, 196, 0.3);
  }
`;

const DeleteButton = styled(Button)`
  background: #e94057;
  color: white;
  
  &:hover {
    box-shadow: 0 3px 10px rgba(233, 64, 87, 0.3);
  }
`;

const SpoilerButton = styled(Button)`
  background: ${props => props.$isSpoiler ? '#ff6b6b' : '#4ecdc4'};
  color: white;
  
  &:hover {
    box-shadow: 0 3px 10px rgba(78, 205, 196, 0.3);
  }
`;

const ProfanityBadge = styled.span`
  padding: 4px 8px;
  border-radius: 5px;
  background: #ff6b6b;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 5px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
`;

const PageButton = styled.button`
  padding: 10px 15px;
  border-radius: 5px;
  border: 1px solid var(--border-color);
  background: ${props => props.$active ? 'var(--gradient-pink)' : 'var(--glass-bg)'};
  color: ${props => props.$active ? 'white' : 'var(--primary-color)'};
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: var(--gradient-pink);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$show ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  color: var(--primary-color);
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: var(--primary-color);
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 50px;
  color: var(--secondary-color);
  font-size: 1.2rem;
`;

const AdminCommentsPage = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [spoilerFilter, setSpoilerFilter] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSpoiler, setEditSpoiler] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [currentPage, searchTerm, spoilerFilter]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        ...(searchTerm && { search: searchTerm }),
        ...(spoilerFilter !== '' && { is_spoiler: spoilerFilter })
      });

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_ALL_COMMENTS)}?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Yorumlar çekilemedi');
      }

      const data = await response.json();
      setComments(data.comments || []);
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Yorum çekme hatası:', error);
      alert('Yorumlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId, commentText) => {
    if (!window.confirm(`Bu yorumu silmek istediğinizden emin misiniz?\n\n"${commentText.substring(0, 50)}..."`)) {
      return;
    }

    try {
      const token = await getCsrfToken();
      const response = await fetch(getApiUrl(API_ENDPOINTS.DELETE_COMMENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ comment_id: commentId }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Yorum silinemedi' }));
        throw new Error(errorData.error || errorData.message || 'Yorum silinemedi');
      }

      const result = await response.json();
      alert(result.message || 'Yorum başarıyla silindi');
      fetchComments();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert(error.message || 'Yorum silinirken bir hata oluştu');
    }
  };

  const handleEdit = (comment) => {
    setEditingComment(comment);
    setEditText(comment.comment_text);
    setEditSpoiler(comment.is_spoiler);
  };

  const handleSaveEdit = async () => {
    if (!editingComment) return;

    try {
      const token = await getCsrfToken();
      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_COMMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          comment_id: editingComment.id,
          comment_text: editText,
          is_spoiler: editSpoiler
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Yorum güncellenemedi');
      }

      alert('Yorum başarıyla güncellendi');
      setEditingComment(null);
      fetchComments();
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      alert('Yorum güncellenirken bir hata oluştu');
    }
  };

  const handleToggleSpoiler = async (comment) => {
    try {
      const token = await getCsrfToken();
      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_COMMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          comment_id: comment.id,
          is_spoiler: !comment.is_spoiler
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Spoiler durumu güncellenemedi');
      }

      fetchComments();
    } catch (error) {
      console.error('Spoiler güncelleme hatası:', error);
      alert('Spoiler durumu güncellenirken bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Geçersiz tarih';
    }
  };

  if (loading && comments.length === 0) {
    return (
      <AdminContainer>
        <Header>
          <Title>Yorum Yönetimi</Title>
        </Header>
        <LoadingText>Yorumlar yükleniyor...</LoadingText>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <Header>
        <Title>Yorum Yönetimi</Title>
      </Header>

      <FiltersContainer>
        <FilterInput
          type="text"
          placeholder="Yorum, kullanıcı veya içerik ara..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
        <FilterSelect
          value={spoilerFilter}
          onChange={(e) => {
            setSpoilerFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">Tüm Yorumlar</option>
          <option value="1">Sadece Spoiler'lar</option>
          <option value="0">Spoiler Olmayanlar</option>
        </FilterSelect>
      </FiltersContainer>

      <CommentsTable>
        <thead>
          <tr>
            <Th>Kullanıcı</Th>
            <Th>İçerik</Th>
            <Th>Yorum</Th>
            <Th>Tarih</Th>
            <Th>İşlemler</Th>
          </tr>
        </thead>
        <tbody>
          {comments.map(comment => {
            const profanityCheck = detectProfanity(comment.comment_text);
            return (
              <tr key={comment.id}>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {comment.avatar_url && (
                      <img 
                        src={comment.avatar_url} 
                        alt={comment.username}
                        style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: '600' }}>{comment.username}</div>
                      {profanityCheck.hasProfanity && (
                        <ProfanityBadge>Küfür Tespit Edildi</ProfanityBadge>
                      )}
                    </div>
                  </div>
                </Td>
                <Td>
                  <div style={{ fontWeight: '600' }}>
                    <a 
                      href={`/${comment.media_type || 'tv'}/${comment.tmdb_id || comment.content_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--gradient-cyan)', textDecoration: 'none' }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {comment.content_title}
                    </a>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#b2bec3', marginTop: '5px' }}>
                    {comment.media_type === 'anime' ? 'Anime' : comment.media_type === 'tv' ? 'Dizi' : 'Film'}
                  </div>
                </Td>
                <Td>
                  <CommentText className={comment.is_spoiler ? 'spoiler' : ''}>
                    {comment.comment_text}
                  </CommentText>
                  {comment.is_spoiler && (
                    <div style={{ fontSize: '0.75rem', color: '#ff6b6b', marginTop: '5px' }}>
                      ⚠️ Spoiler İçerir
                    </div>
                  )}
                </Td>
                <Td>
                  <div style={{ fontSize: '0.9rem' }}>{formatDate(comment.created_at)}</div>
                </Td>
                <Td>
                  <ActionButtons>
                    <EditButton onClick={() => handleEdit(comment)}>Düzenle</EditButton>
                    <SpoilerButton 
                      $isSpoiler={comment.is_spoiler}
                      onClick={() => handleToggleSpoiler(comment)}
                    >
                      {comment.is_spoiler ? 'Spoiler Kaldır' : 'Spoiler Ekle'}
                    </SpoilerButton>
                    <DeleteButton onClick={() => handleDelete(comment.id, comment.comment_text)}>
                      Sil
                    </DeleteButton>
                  </ActionButtons>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </CommentsTable>

      {comments.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#b2bec3' }}>
          Yorum bulunamadı.
        </div>
      )}

      {totalPages > 1 && (
        <Pagination>
          <PageButton
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Önceki
          </PageButton>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <PageButton
                key={pageNum}
                $active={currentPage === pageNum}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </PageButton>
            );
          })}
          
          <PageButton
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Sonraki
          </PageButton>
        </Pagination>
      )}

      <EditModal $show={editingComment !== null}>
        <ModalContent>
          <ModalTitle>Yorum Düzenle</ModalTitle>
          <ModalTextarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="Yorum metni..."
          />
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-color)' }}>
              <input
                type="checkbox"
                checked={editSpoiler}
                onChange={(e) => setEditSpoiler(e.target.checked)}
              />
              <span>Spoiler içerir</span>
            </label>
          </div>
          <ModalButtons>
            <Button
              onClick={() => setEditingComment(null)}
              style={{ background: '#636e72', color: 'white' }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveEdit}
              style={{ background: 'var(--gradient-pink)', color: 'white' }}
            >
              Kaydet
            </Button>
          </ModalButtons>
        </ModalContent>
      </EditModal>
    </AdminContainer>
  );
};

export default AdminCommentsPage;

